import {
  type QueryClient,
  useInfiniteQuery,
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { chatService } from "@/services/chat.service";
import { collectionsService } from "@/services/collections.service";
import { useAuth } from "@/contexts/AuthContext";
import {
  collectionReverseLookupKeysForItemKeys,
  collectionReverseLookupKeysForItems,
} from "./collectionInvalidation";
import type {
  Collection,
  CollectionInviteLinks,
  CollectionItem,
  CollectionItemType,
  CollectionMember,
  CollectionRole,
  CreateCollectionRequest,
  CursorPage,
  UpdateCollectionRequest,
} from "@/types/collections";

// ---------------------------------------------------------------------------
// Query keys — hierarchical for granular invalidation
// ---------------------------------------------------------------------------

export const COLLECTION_KEYS = {
  all: ["collections"] as const,
  personal: () => [...COLLECTION_KEYS.all, "personal"] as const,
  details: () => [...COLLECTION_KEYS.all, "detail"] as const,
  detail: (id: string, skipAuth = false) =>
    [...COLLECTION_KEYS.details(), id, skipAuth ? "public" : "auth"] as const,
  stashes: (id: string, skipAuth = false) =>
    [...COLLECTION_KEYS.detail(id, skipAuth), "stashes"] as const,
  entities: (id: string, skipAuth = false) =>
    [...COLLECTION_KEYS.detail(id, skipAuth), "entities"] as const,
  members: (id: string) =>
    [...COLLECTION_KEYS.details(), id, "auth", "members"] as const,
  forStash: (stashId: string) => ["collections-for-stash", stashId] as const,
  forEntity: (entityId: string) => ["collections-for-entity", entityId] as const,
  public: () => [...COLLECTION_KEYS.all, "public"] as const,
  recommendations: (id: string) =>
    [...COLLECTION_KEYS.detail(id), "recommendations"] as const,
  inviteLink: (id: string) =>
    [...COLLECTION_KEYS.details(), id, "invite-link"] as const,
};

/** Account catalogs share summaries, membership and personal-collection covers. */
export function invalidateCollectionCatalog(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: COLLECTION_KEYS.personal() });
}

// ---------------------------------------------------------------------------
// Query hooks
// ---------------------------------------------------------------------------

/**
 * The collection inventory Home and the menu read: the user's personal collections
 * ("Favourites" and "Done", found by title as natively in CollectionStore.readPersonal;
 * with duplicates the lowest id wins) and the catalog's size and first covers.
 * One read of every catalog page, shared by everyone who asks.
 */
export type PersonalCollectionKind = "favourites" | "done";

interface PersonalCollectionSummary {
  id: string;
  items: number;
  covers: string[];
}

export interface CollectionInventory {
  /** Every collection the account can see, as read. */
  catalog: Collection[];
  favourites: PersonalCollectionSummary | null;
  done: PersonalCollectionSummary | null;
  count: number;
  covers: string[];
}
const PERSONAL_TITLES: Record<PersonalCollectionKind, string> = { favourites: "Favourites", done: "Done" };

/** Shown as "Favorites", as natively (Collection.displayTitle); the stored title stays. */
export const collectionDisplayTitle = (title: string) => (title === PERSONAL_TITLES.favourites ? "Favorites" : title);

const readCollectionInventory = async (): Promise<CollectionInventory> => {
  const catalog: Collection[] = [];
  let cursor: string | undefined;
  do {
    const page = await collectionsService.listCollections({ cursor, limit: 50 });
    catalog.push(...page.items);
    cursor = page.next_cursor ?? undefined;
  } while (cursor);
  const personal = (title: string): PersonalCollectionSummary | null => {
    const collection = catalog
      .filter(item => item.role === "owner" && item.title === title)
      .sort((a, b) => a.collection_id.localeCompare(b.collection_id))[0];
    return collection
      ? { id: collection.collection_id, items: collection.item_count, covers: (collection.cover_thumbnails ?? []).slice(0, 4) }
      : null;
  };
  return {
    catalog,
    favourites: personal(PERSONAL_TITLES.favourites),
    done: personal(PERSONAL_TITLES.done),
    count: catalog.length,
    covers: catalog.flatMap(collection => collection.cover_thumbnails?.slice(0, 1) ?? []).slice(0, 4),
  };
};

function useCollectionInventoryQuery<T>(select: (inventory: CollectionInventory) => T) {
  const { isAuthenticated, isLoading } = useAuth();
  return useQuery({
    queryKey: COLLECTION_KEYS.personal(),
    queryFn: readCollectionInventory,
    select,
    enabled: isAuthenticated && !isLoading,
    staleTime: 5 * 60_000,
  });
}

const selectInventory = (inventory: CollectionInventory) => inventory;
const selectCatalog = (inventory: CollectionInventory) => inventory.catalog;
const selectFavourites = (inventory: CollectionInventory) => inventory.favourites?.id ?? null;
const selectDone = (inventory: CollectionInventory) => inventory.done?.id ?? null;
const selectWritable = (inventory: CollectionInventory) =>
  inventory.catalog
    .filter(collection => collection.role === "owner" || collection.role === "editor")
    .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());

const NO_COLLECTIONS: Collection[] = [];

export const useCollectionInventory = () => useCollectionInventoryQuery(selectInventory);

/** Every collection the account can see (its own, shared, followed), read once with the inventory. */
export const useCollectionCatalog = () => useCollectionInventoryQuery(selectCatalog);

/** The collections the account can add to, most recently updated first, for the pickers. */
export function useWritableCollections() {
  const { data, isLoading } = useCollectionInventoryQuery(selectWritable);
  return { collections: data ?? NO_COLLECTIONS, isLoading };
}

/** The id of the user's Favorites or Done collection (null when it doesn't exist yet). */
export const usePersonalCollection = (kind: PersonalCollectionKind) =>
  useCollectionInventoryQuery(kind === "favourites" ? selectFavourites : selectDone);

/** Favorites and Done are ordinary private collections, created on the first action. */
export function usePersonalCollectionItem(item: { type: CollectionItemType; id: string }, enabled: boolean) {
  const queryClient = useQueryClient();
  const inventory = useCollectionInventory();
  const stashMembership = useCollectionsForStash(item.type === "stash" ? item.id : undefined, enabled);
  const entityMembership = useCollectionsForEntity(item.type === "entity" ? item.id : undefined, enabled);
  const membership = item.type === "stash" ? stashMembership : entityMembership;
  const create = useCreateCollection();
  const add = useAddToCollection();
  const remove = useRemoveFromCollection();
  const writing = useIsMutating({ mutationKey: ["personal-collection-item"] }) > 0;
  const mutation = useMutation({
    mutationKey: ["personal-collection-item"],
    // Two first actions on this device must not create competing collections.
    scope: { id: "personal-collection-item" },
    mutationFn: async ({ kind, selected }: { kind: PersonalCollectionKind; selected: boolean }) => {
      const current = await queryClient.fetchQuery({
        queryKey: COLLECTION_KEYS.personal(), queryFn: readCollectionInventory, staleTime: 5 * 60_000,
      });
      let collectionId = current[kind]?.id;
      if (!collectionId && selected) {
        const collection = await create.mutateAsync({ title: PERSONAL_TITLES[kind], visibility: "private" });
        collectionId = collection.collection_id;
        // Reuse the catalog owner on the next action, including one already queued.
        await queryClient.fetchQuery({ queryKey: COLLECTION_KEYS.personal(), queryFn: readCollectionInventory, staleTime: 0 });
      }
      if (!collectionId) return;
      if (selected) await add.mutateAsync({ collectionId, items: [item] });
      else await remove.mutateAsync({ collectionId, itemKeys: [`${item.type}#${item.id}`] });
    },
  });
  const contains = (kind: PersonalCollectionKind) => {
    const id = inventory.data?.[kind]?.id;
    return Boolean(id && membership.data?.items.some(collection => collection.collection_id === id));
  };
  return {
    favourite: contains("favourites"),
    done: contains("done"),
    ready: inventory.isSuccess && membership.isSuccess && !membership.isFetching,
    writing,
    set: mutation.mutateAsync,
  };
}

export function useCollection(id: string | undefined, skipAuth = false, invite?: string) {
  return useQuery({
    queryKey: COLLECTION_KEYS.detail(id!, skipAuth),
    queryFn: () => collectionsService.getCollection(id!, skipAuth, invite),
    enabled: !!id,
    staleTime: 60_000,
  });
}

export function useCollectionStashes(id: string | undefined, skipAuth = false, invite?: string) {
  return useQuery({
    queryKey: COLLECTION_KEYS.stashes(id!, skipAuth),
    queryFn: () => collectionsService.listStashes(id!, { skipAuth, invite }),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useCollectionEntities(id: string | undefined, skipAuth = false, invite?: string) {
  return useQuery({
    queryKey: COLLECTION_KEYS.entities(id!, skipAuth),
    queryFn: () =>
      collectionsService.listItems(id!, {
        hydrate: true, item_type: "entity", skipAuth, invite,
      }),
    enabled: !!id,
    staleTime: 30_000,
  });
}

/** Which of a collection's stashes match a search, collaborators' included. */
export function useCollectionSearch(id: string | undefined, term: string) {
  return useQuery({
    queryKey: ["search", "collection", id, term],
    queryFn: async () => (await chatService.searchCollection(term, id!)).ids,
    enabled: !!id && term.length > 0,
    staleTime: 5 * 60_000,
  });
}

export function useCollectionRecommendations(id: string | undefined) {
  return useQuery({
    queryKey: COLLECTION_KEYS.recommendations(id!),
    queryFn: () => collectionsService.getRecommendations(id!),
    enabled: !!id,
    staleTime: 5 * 60_000,
  });
}

export function useCollectionMembers(id: string | undefined) {
  return useQuery({
    queryKey: COLLECTION_KEYS.members(id!),
    queryFn: () => collectionsService.listMembers(id!),
    enabled: !!id,
    staleTime: 60_000,
  });
}

export function useCollectionInviteLink(collectionId: string | undefined, isOwner: boolean) {
  return useQuery({
    queryKey: COLLECTION_KEYS.inviteLink(collectionId!),
    queryFn: () => collectionsService.getInviteLink(collectionId!),
    enabled: !!collectionId && isOwner,
    staleTime: 60_000,
  });
}

export function useGenerateInviteLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      collectionId,
      role,
    }: {
      collectionId: string;
      role: CollectionRole;
    }) => collectionsService.generateInviteLink(collectionId, role),
    onSuccess: (link, { collectionId, role }) => {
      queryClient.setQueryData<{ invite_links: CollectionInviteLinks }>(
        COLLECTION_KEYS.inviteLink(collectionId),
        (prev) => ({
          invite_links: {
            viewer: prev?.invite_links?.viewer ?? null,
            editor: prev?.invite_links?.editor ?? null,
            [role]: link,
          } as CollectionInviteLinks,
        }),
      );
      queryClient.invalidateQueries({ queryKey: COLLECTION_KEYS.detail(collectionId) });
    },
  });
}

export function useRevokeInviteLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      collectionId,
      role,
    }: {
      collectionId: string;
      role: CollectionRole;
    }) => collectionsService.revokeInviteLink(collectionId, role),
    onSuccess: (_data, { collectionId, role }) => {
      queryClient.setQueryData<{ invite_links: CollectionInviteLinks }>(
        COLLECTION_KEYS.inviteLink(collectionId),
        (prev) => ({
          invite_links: {
            viewer: prev?.invite_links?.viewer ?? null,
            editor: prev?.invite_links?.editor ?? null,
            [role]: null,
          } as CollectionInviteLinks,
        }),
      );
      queryClient.invalidateQueries({ queryKey: COLLECTION_KEYS.detail(collectionId) });
    },
  });
}

export function useCollectionsForStash(stashId: string | undefined, enabled = true) {
  const { isAuthenticated, isLoading } = useAuth();
  return useQuery({
    queryKey: COLLECTION_KEYS.forStash(stashId!),
    queryFn: () => collectionsService.getCollectionsForStash(stashId!),
    enabled: enabled && !!stashId && isAuthenticated && !isLoading,
    staleTime: 60_000,
  });
}

export function useCollectionsForEntity(entityId: string | undefined, enabled = true) {
  const { isAuthenticated, isLoading } = useAuth();
  return useQuery({
    queryKey: COLLECTION_KEYS.forEntity(entityId!),
    queryFn: () => collectionsService.getCollectionsForEntity(entityId!),
    enabled: enabled && !!entityId && isAuthenticated && !isLoading,
    staleTime: 60_000,
  });
}

export function usePublicCollections(enabled = true) {
  return useInfiniteQuery({
    queryKey: COLLECTION_KEYS.public(),
    enabled,
    queryFn: ({ pageParam }) =>
      collectionsService.listPublicCollections({ cursor: pageParam, limit: 30 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    staleTime: 120_000,
  });
}

// ---------------------------------------------------------------------------
// Mutation hooks
// ---------------------------------------------------------------------------

export function useCreateCollection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateCollectionRequest) =>
      collectionsService.createCollection(input),
    onSuccess: () => {
      invalidateCollectionCatalog(queryClient);
    },
  });
}

export function useUpdateCollection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateCollectionRequest }) =>
      collectionsService.updateCollection(id, input),
    onMutate: async ({ id, input }) => {
      await queryClient.cancelQueries({ queryKey: COLLECTION_KEYS.detail(id) });
      const previous = queryClient.getQueryData<Collection>(COLLECTION_KEYS.detail(id));
      if (previous) {
        queryClient.setQueryData(COLLECTION_KEYS.detail(id), { ...previous, ...input });
      }
      return { previous, id };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(COLLECTION_KEYS.detail(ctx.id), ctx.previous);
      }
    },
    onSettled: (_data, _err, { id }) => {
      queryClient.invalidateQueries({ queryKey: COLLECTION_KEYS.detail(id) });
      invalidateCollectionCatalog(queryClient);
    },
  });
}

export function useDeleteCollection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => collectionsService.deleteCollection(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COLLECTION_KEYS.all });
    },
  });
}

export function useAddToCollection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      collectionId,
      items,
    }: {
      collectionId: string;
      items: Array<{ type: CollectionItemType; id: string }>;
    }) => collectionsService.addItems(collectionId, items),
    onMutate: async ({ collectionId, items }) => {
      await queryClient.cancelQueries({ queryKey: COLLECTION_KEYS.detail(collectionId) });
      const previous = queryClient.getQueryData<Collection>(COLLECTION_KEYS.detail(collectionId));
      if (previous) {
        const stashAdded = items.filter((i) => i.type === "stash").length;
        queryClient.setQueryData(COLLECTION_KEYS.detail(collectionId), {
          ...previous,
          item_count: previous.item_count + items.length,
          stash_count: (previous.stash_count ?? 0) + stashAdded,
        });
      }
      return { previous, collectionId };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(COLLECTION_KEYS.detail(ctx.collectionId), ctx.previous);
      }
    },
    onSettled: (_data, _err, { collectionId, items }) => {
      queryClient.invalidateQueries({ queryKey: COLLECTION_KEYS.stashes(collectionId) });
      queryClient.invalidateQueries({ queryKey: COLLECTION_KEYS.entities(collectionId) });
      invalidateCollectionCatalog(queryClient);
      queryClient.invalidateQueries({ queryKey: COLLECTION_KEYS.recommendations(collectionId) });
      for (const queryKey of collectionReverseLookupKeysForItems(items)) {
        queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}

export function useRemoveFromCollection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      collectionId,
      itemKeys,
    }: {
      collectionId: string;
      itemKeys: string[];
    }) => collectionsService.removeItems(collectionId, itemKeys),
    // React Query serializes onMutate callbacks, so each rapid mutation sees the
    // cache state left by the previous one. cancelQueries + getQueryData is safe.
    onMutate: async ({ collectionId, itemKeys }) => {
      await queryClient.cancelQueries({ queryKey: COLLECTION_KEYS.detail(collectionId) });
      await queryClient.cancelQueries({ queryKey: COLLECTION_KEYS.stashes(collectionId) });
      await queryClient.cancelQueries({ queryKey: COLLECTION_KEYS.entities(collectionId) });

      // Optimistic update: detail count
      const previous = queryClient.getQueryData<Collection>(COLLECTION_KEYS.detail(collectionId));
      if (previous) {
        const stashRemoved = itemKeys.filter((k) => k.startsWith("stash#")).length;
        queryClient.setQueryData(COLLECTION_KEYS.detail(collectionId), {
          ...previous,
          item_count: Math.max(0, previous.item_count - itemKeys.length),
          stash_count: Math.max(0, (previous.stash_count ?? 0) - stashRemoved),
        });
      }

      const itemKeySet = new Set(itemKeys);

      // Optimistic update: remove from stashes cache
      const previousStashes = queryClient.getQueryData<
        CursorPage<CollectionItem>
      >(COLLECTION_KEYS.stashes(collectionId));
      if (previousStashes) {
        queryClient.setQueryData(COLLECTION_KEYS.stashes(collectionId), {
          ...previousStashes,
          items: previousStashes.items.filter((item) => !itemKeySet.has(item.item_key)),
        });
      }

      // Optimistic update: remove from entities cache
      const previousEntities = queryClient.getQueryData<
        CursorPage<CollectionItem>
      >(COLLECTION_KEYS.entities(collectionId));
      if (previousEntities) {
        queryClient.setQueryData(COLLECTION_KEYS.entities(collectionId), {
          ...previousEntities,
          items: previousEntities.items.filter((item) => !itemKeySet.has(item.item_key)),
        });
      }

      return { previous, previousStashes, previousEntities, collectionId };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(COLLECTION_KEYS.detail(ctx.collectionId), ctx.previous);
      }
      if (ctx?.previousStashes) {
        queryClient.setQueryData(COLLECTION_KEYS.stashes(ctx.collectionId), ctx.previousStashes);
      }
      if (ctx?.previousEntities) {
        queryClient.setQueryData(COLLECTION_KEYS.entities(ctx.collectionId), ctx.previousEntities);
      }
    },
    onSettled: (_data, _err, { collectionId, itemKeys }) => {
      queryClient.invalidateQueries({ queryKey: COLLECTION_KEYS.stashes(collectionId) });
      queryClient.invalidateQueries({ queryKey: COLLECTION_KEYS.entities(collectionId) });
      invalidateCollectionCatalog(queryClient);
      for (const queryKey of collectionReverseLookupKeysForItemKeys(itemKeys)) {
        queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}

export function useInviteMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      collectionId,
      userId,
      role,
    }: {
      collectionId: string;
      userId: string;
      role: CollectionRole;
    }) => collectionsService.inviteMember(collectionId, userId, role),
    onSettled: (_data, _err, { collectionId }) => {
      queryClient.invalidateQueries({ queryKey: COLLECTION_KEYS.members(collectionId) });
      invalidateCollectionCatalog(queryClient);
      queryClient.invalidateQueries({ queryKey: COLLECTION_KEYS.detail(collectionId) });
    },
  });
}

export function useAcceptInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      collectionId,
      userId,
    }: {
      collectionId: string;
      userId: string;
    }) => collectionsService.acceptInvite(collectionId, userId),
    onSettled: (_data, _err, { collectionId }) => {
      queryClient.invalidateQueries({ queryKey: COLLECTION_KEYS.detail(collectionId) });
      queryClient.invalidateQueries({ queryKey: COLLECTION_KEYS.members(collectionId) });
      invalidateCollectionCatalog(queryClient);
    },
  });
}

export function useDeclineInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      collectionId,
      userId,
    }: {
      collectionId: string;
      userId: string;
    }) => collectionsService.declineInvite(collectionId, userId),
    onSettled: (_data, _err, { collectionId }) => {
      queryClient.invalidateQueries({ queryKey: COLLECTION_KEYS.detail(collectionId) });
      queryClient.invalidateQueries({ queryKey: COLLECTION_KEYS.members(collectionId) });
      invalidateCollectionCatalog(queryClient);
    },
  });
}

export function useInviteExternal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      collectionId,
      email,
      role,
    }: {
      collectionId: string;
      email: string;
      role: CollectionRole;
    }) => collectionsService.inviteExternal(collectionId, email, role),
    onSettled: (_data, _err, { collectionId }) => {
      queryClient.invalidateQueries({ queryKey: COLLECTION_KEYS.members(collectionId) });
    },
  });
}

export function useUpdateMemberRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      collectionId,
      userId,
      role,
    }: {
      collectionId: string;
      userId: string;
      role: CollectionRole;
    }) => collectionsService.updateMemberRole(collectionId, userId, role),
    onSettled: (_data, _err, { collectionId }) => {
      queryClient.invalidateQueries({ queryKey: COLLECTION_KEYS.members(collectionId) });
      invalidateCollectionCatalog(queryClient);
    },
  });
}

export function useRemoveMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      collectionId,
      userId,
    }: {
      collectionId: string;
      userId: string;
    }) => collectionsService.removeMember(collectionId, userId),
    onSettled: (_data, _err, { collectionId }) => {
      queryClient.invalidateQueries({ queryKey: COLLECTION_KEYS.members(collectionId) });
      invalidateCollectionCatalog(queryClient);
      queryClient.invalidateQueries({ queryKey: COLLECTION_KEYS.detail(collectionId) });
    },
  });
}

export function useFollowCollection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (collectionId: string) =>
      collectionsService.followCollection(collectionId),
    onSuccess: (_data, collectionId) => {
      queryClient.invalidateQueries({ queryKey: COLLECTION_KEYS.detail(collectionId) });
      invalidateCollectionCatalog(queryClient);
      queryClient.invalidateQueries({ queryKey: COLLECTION_KEYS.public() });
    },
  });
}

export function useUnfollowCollection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (collectionId: string) =>
      collectionsService.unfollowCollection(collectionId),
    onSuccess: (_data, collectionId) => {
      queryClient.invalidateQueries({ queryKey: COLLECTION_KEYS.detail(collectionId) });
      invalidateCollectionCatalog(queryClient);
      queryClient.invalidateQueries({ queryKey: COLLECTION_KEYS.public() });
    },
  });
}
